import React, { useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Fab, { fabClasses } from '@mui/material/Fab'
import { PropTypes } from 'prop-types'
import { useSelector } from 'react-redux'
import styled from '@emotion/styled'

import {
  mapImageLayer,
  toggleLayerVisibility
} from '../../controller/esriMapController'
import './MapView.scss'
import PedestrianIcon from '../../icons/PedestrianIcon'
import {
  AUTO,
  BICYCLE,
  FREIGHT,
  PEDESTRIAN,
  TRANSIT
} from '../../constants/modalTiers'
import AutoIcon from '../../icons/AutoIcon'
import FreightIcon from '../../icons/FreightIcon'
import TransitIcon from '../../icons/TransitIcon'
import BicycleIcon from '../../icons/BicycleIcon'
import LandUseContext from '../../icons/LandUseContext'
import { LightTooltip } from '../CriteriaSelection/ModalCriteria'
import {
  AUTO_LAYER,
  BIKE_LAYER,
  COUNTY_BOUNDARIES_LAYER,
  FREIGHT_LAYER,
  LANDUSE_LAYER,
  PEDESTRIAN_LAYER,
  TRANSIT_LAYER
} from '../../constants/layerFieldNames'

const getIcon = (field, active, hover) => {
  switch (field) {
    case PEDESTRIAN:
      return <PedestrianIcon fill={active || hover ? '#FFFFFF' : '#262626'} />

    case AUTO:
      return <AutoIcon fill={active || hover ? '#FFFFFF' : '#262626'} />

    case FREIGHT:
      return <FreightIcon fill={active || hover ? '#FFFFFF' : '#262626'} />

    case TRANSIT:
      return <TransitIcon fill={active || hover ? '#FFFFFF' : '#262626'} />

    case 'Bike':
      return <BicycleIcon fill={active || hover ? '#FFFFFF' : '#262626'} />

    case 'Land Use Context':
      return <LandUseContext fill={active || hover ? '#FFFFFF' : '#262626'} />

    default:
  }
}
export const StyledFab = styled(Fab)(
  ({ backgroundcolor, borderradius = 15, width, height }) => ({
    border: 0,
    borderRadius: borderradius,
    cursor: 'pointer',
    backgroundColor: backgroundcolor,
    width,
    height
  })
)

export const FabWrapper = ({
  children,
  right = 0,
  width,
  top,
  left,
  zIndex
}) => {
  const { drawerOpen } = useSelector((state) => state.mainTool)
  return (
    <Box
      sx={{
        position: 'absolute',
        left: right ? 'none' : left || '15px',
        right,
        bottom: drawerOpen ? 550 : 20,
        transition: 'bottom .5s ease-in-out',
        transitionDelay: '.1s',
        width,
        top,
        zIndex
      }}
    >
      {children}
    </Box>
  )
}

export default function LayerSelector() {
  const [layers, setLayers] = React.useState([])
  const layerOrder = [
    LANDUSE_LAYER,
    PEDESTRIAN_LAYER,
    BIKE_LAYER,
    TRANSIT_LAYER,
    AUTO_LAYER,
    FREIGHT_LAYER
  ]
  const { mapLoaded, showClearSegmentsStartManualModal } = useSelector(
    (state) => state.appSlice
  )

  useEffect(() => {
    if (mapLoaded) {
      const layersCopy = []
      mapImageLayer.sublayers.forEach((layer) => {
        const layerRow = {
          title: layer.title,
          visibility: layer.visible,
          id: layer.id
        }
        if (layer.title !== COUNTY_BOUNDARIES_LAYER) {
          setLayers((rows) => [...rows, layerRow])
          layersCopy.push(layerRow)
        }
      })

      // Set layer order
      layerOrder.forEach((layerName, orderIndex) => {
        const layer = layersCopy.find((layer) => layer.title === layerName)
        if (layer) {
          const index = layersCopy.findIndex(
            (layer) => layer.title === layerName
          )
          layersCopy.splice(index, 1)
          layersCopy.splice(orderIndex, 0, layer)
        }
      })
      setLayers(layersCopy)
    }
  }, [mapLoaded])

  return (
    <FabWrapper width="450px" zIndex={1000}>
      <Box sx={{ '& > :not(style)': { m: 1 } }} className="layerSelector">
        {layers.map((layer) => {
          return (
            <CustomFab
              name={layer.title}
              id={layer.id}
              key={`${layer.title}-layer-icon`}
            />
          )
        })}
      </Box>
    </FabWrapper>
  )
}

function CustomFab({ name, id }) {
  const [active, setActive] = useState(name === 'Land Use Context')
  const [hover, setHover] = useState(false)
  function toggleLayer() {
    toggleLayerVisibility(id, !active)

    setActive(!active)
  }

  return (
    <LightTooltip title={name} placement="top">
      <StyledFab
        color="primary"
        aria-label="add"
        onClick={toggleLayer}
        width={56}
        height={56}
        left={20}
        backgroundcolor={active ? '' : 'white'}
        disableRipple={true}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        {getIcon(name, active, hover)}
      </StyledFab>
    </LightTooltip>
  )
}
CustomFab.propTypes = {
  name: PropTypes.string.isRequired,
  id: PropTypes.number.isRequired
}
FabWrapper.propTypes = {
  children: PropTypes.node,
  right: PropTypes.string,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  top: PropTypes.string,
  left: PropTypes.string,
  zIndex: PropTypes.number
}
